import React, { useState, useEffect } from "react";
import BaseTailoringQuestions from "../shared/BaseTailoringQuestions";
import type { TailoringQuestion } from "../shared/BaseTailoringQuestions";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { AlertCircle, FileText, FileSignature } from "lucide-react";
import subsequentEventsData from "../../constants/ConcludingProcedures/Subsequent_Events.json";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface SubsequentEventsLandingProps {
  onSelect?: (section: string) => void;
}

// Transform JSON structure to TailoringQuestion format
function transformJSONToQuestions(jsonData: any): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];
  const data = jsonData.subsequentEvents;

  // Helper to create a question
  const createQuestion = (
    id: string,
    questionText: string,
    type: "radio" | "text",
    options?: string[],
    followUp?: TailoringQuestion["follow_up"]
  ): TailoringQuestion => {
    return {
      id,
      question: questionText,
      type,
      options,
      follow_up: followUp,
    };
  };

  // SE-1: Have any of the below occurred... (Multiple checkbox - convert to separate yes/no questions)
  if (data.question1) {
    const q = data.question1;
    // Create the main question text (header only, no options)
    questions.push(
      createQuestion("SE-1", q.label, "radio", [])
    );
    
    // Create separate yes/no questions for each checkbox option with follow-ups
    q.options?.forEach((opt: any, index: number) => {
      const questionId = `SE-1-${index + 1}`;
      let followUp: TailoringQuestion["follow_up"] | undefined = undefined;
      
      // First checkbox (index 0) - Events between FS and Audit Report
      if (index === 0 && jsonData.eventsBetweenFSDandAuditReport) {
        const section = jsonData.eventsBetweenFSDandAuditReport;
        if (section.documentationProcedures) {
          followUp = {
            id: "SE-1-1-doc",
            question: section.documentationProcedures.label,
            type: "text",
            condition: "Yes",
          };
        }
      }
      
      // Second checkbox (index 1) - Events after Audit Report
      if (index === 1 && jsonData.eventsAfterAuditReport) {
        const section = jsonData.eventsAfterAuditReport;
        if (section.documentationProcedures) {
          followUp = {
            id: "SE-1-2-doc",
            question: section.documentationProcedures.label,
            type: "text",
            condition: "Yes",
          };
        }
      }
      
      questions.push(
        createQuestion(
          questionId,
          opt.label,
          "radio",
          ["Yes", "No"],
          followUp
        )
      );
    });
  }

  // SE-2: We confirm that...
  if (data.question2) {
    const q = data.question2;
    const options = q.options?.map((opt: any) => opt.label) || [];
    
    // Find the "Other response" option
    const otherOption = q.options?.find((opt: any) => opt.textbox);
    const followUp: TailoringQuestion["follow_up"] = otherOption
      ? {
          id: "SE-2-other",
          question: "Please provide details:",
          type: "text",
          condition: "Other response",
        }
      : undefined;
    
    questions.push(
      createQuestion("SE-2", q.label, "radio", options, followUp)
    );
  }

  // SE-3: AS 4 Assessment (follow-up to SE-1-1 when Yes)
  if (jsonData.eventsBetweenFSDandAuditReport?.as4Assessment) {
    const as4 = jsonData.eventsBetweenFSDandAuditReport.as4Assessment;
    
    // Main AS4 question
    if (as4.question) {
      let as4FollowUp: TailoringQuestion["follow_up"] | undefined = undefined;
      
      // Build nested follow-ups for ifYes
      if (as4.ifYes) {
        const ifYesFollowUp: TailoringQuestion["follow_up"] = {
          id: "SE-3-yes-adjustment",
          question: as4.ifYes.label,
          type: "radio",
          condition: "Yes",
          options: ["Yes", "No"],
        };
        
        // Add note as a separate question if ifYes.ifYes exists
        if (as4.ifYes.ifYes?.note) {
          questions.push(
            createQuestion(
              "SE-3-yes-adjustment-note",
              `If you answered 'Yes' to 'Have the events been adjusted in the financial statements?', note:\n\n${as4.ifYes.ifYes.note}`,
              "text"
            )
          );
        }
        
        as4FollowUp = ifYesFollowUp;
      }
      
      // Build nested follow-ups for ifNo
      if (as4.ifNo) {
        const ifNoFollowUp: TailoringQuestion["follow_up"] = {
          id: "SE-3-no-material",
          question: as4.ifNo.label,
          type: "radio",
          condition: "No",
          options: ["Yes", "No"],
        };
        
        // Add note as a separate question if ifNo.ifYes exists
        if (as4.ifNo.ifYes?.note) {
          questions.push(
            createQuestion(
              "SE-3-no-material-note",
              `If you answered 'Yes' to 'Do the events represent material changes?', note:\n\n${as4.ifNo.ifYes.note}`,
              "text"
            )
          );
        }
        
        // Use ifNo follow-up if as4FollowUp is not set
        if (!as4FollowUp) {
          as4FollowUp = ifNoFollowUp;
        }
      }
      
      questions.push(
        createQuestion(
          "SE-3",
          as4.question.label,
          "radio",
          ["Yes", "No"],
          as4FollowUp
        )
      );
    }
  }

  // SE-4: Events Table (follow-up to SE-1-1 when Yes)
  if (jsonData.eventsBetweenFSDandAuditReport?.eventsTable) {
    const table = jsonData.eventsBetweenFSDandAuditReport.eventsTable;
    const columns = table.columns || [];
    
    questions.push({
      id: "SE-4",
      question: table.label,
      type: "table",
      tableConfig: {
        columns: columns.map((col: any) => ({
          key: col.key,
          label: col.label,
          type: col.type || "text",
        })),
        allowAdd: table.allowAdd !== false,
        allowDelete: table.allowDelete !== false,
      },
    });
  }

  // SE-5: Management intends to amend (follow-up to SE-1-2 when Yes)
  if (jsonData.eventsAfterAuditReport?.managementIntendsToAmend) {
    const amend = jsonData.eventsAfterAuditReport.managementIntendsToAmend;
    
    let amendFollowUp: TailoringQuestion["follow_up"] | undefined = undefined;
    
    // Build nested follow-ups for ifYes
    if (amend.ifYes?.lawPermitsRestriction) {
      const lawPermits = amend.ifYes.lawPermitsRestriction;
      
      const lawPermitsFollowUp: TailoringQuestion["follow_up"] = {
        id: "SE-5-law-permits",
        question: lawPermits.label,
        type: "radio",
        condition: "Yes",
        options: ["Yes", "No"],
      };
      
      // Add options and documentation for ifYes.ifYes
      if (lawPermits.ifYes) {
        if (lawPermits.ifYes.options) {
          const optionsText = lawPermits.ifYes.options.map((opt: any) => `- ${opt.label}`).join('\n');
          questions.push(
            createQuestion(
              "SE-5-law-permits-yes-options",
              `If you answered 'Yes' to 'Does law/regulation permit restricting amendment?', the auditor shall either:\n\n${optionsText}`,
              "text"
            )
          );
        }
        
        if (lawPermits.ifYes.documentation) {
          questions.push(
            createQuestion(
              "SE-5-law-permits-yes-doc",
              lawPermits.ifYes.documentation.label,
              "text"
            )
          );
        }
      }
      
      // Add steps and documentation for ifYes.ifNo
      if (lawPermits.ifNo) {
        if (lawPermits.ifNo.steps) {
          const stepsText = lawPermits.ifNo.steps.map((step: string, idx: number) => `${idx + 1}. ${step}`).join('\n');
          questions.push(
            createQuestion(
              "SE-5-law-permits-no-steps",
              `If you answered 'No' to 'Does law/regulation permit restricting amendment?', the auditor shall:\n\n${stepsText}`,
              "text"
            )
          );
        }
        
        if (lawPermits.ifNo.documentation) {
          questions.push(
            createQuestion(
              "SE-5-law-permits-no-doc",
              lawPermits.ifNo.documentation.label,
              "text"
            )
          );
        }
      }
      
      amendFollowUp = lawPermitsFollowUp;
    }
    
    // Build follow-up for ifNo
    if (amend.ifNo?.documentation) {
      const ifNoFollowUp: TailoringQuestion["follow_up"] = {
        id: "SE-5-no-doc",
        question: amend.ifNo.documentation.label,
        type: "text",
        condition: "No",
      };
      
      if (!amendFollowUp) {
        amendFollowUp = ifNoFollowUp;
      }
    }
    
    questions.push(
      createQuestion(
        "SE-5",
        amend.label,
        "radio",
        ["Yes", "No"],
        amendFollowUp
      )
    );
  }

  return questions;
}

const WORKSPACE_NAME = "Subsequent Events";

export default function SubsequentEventsLanding(_props: SubsequentEventsLandingProps) {
  const [questions, setQuestions] = useState<TailoringQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        const jsonData = subsequentEventsData;
        const transformedQuestions = transformJSONToQuestions(jsonData);
        setQuestions(transformedQuestions);
      } catch (err) {
        console.error("Failed to load questions:", err);
        setError(err instanceof Error ? err.message : "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Subsequent Events</h2>
          <p className="text-white/60">Loading questions...</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions from JSON file...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Subsequent Events</h2>
          <p className="text-white/60">Error loading questions</p>
        </div>
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Subsequent Events</h2>
          <p className="text-white/60">No questions found</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <FileText className="h-8 w-8 mx-auto mb-4 text-white/40" />
              <p>No questions available. Please check the JSON file.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 flex-wrap px-6 pt-2">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button variant="outline" size="sm" onClick={() => setSignoffDialogOpen(true)} className="border-white/20 text-white hover:bg-white/10">
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <BaseTailoringQuestions
        areaName="SUBSEV"
        title="Subsequent Events"
        description="Answer the following questions based on subsequent events requirements."
        questions={questions}
        onComplete={(answers) => {
          console.log("Subsequent Events answers:", answers);
        }}
      />
    </div>
  );
}
